import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
  withCredentials: true,
});

// OFFERS4BRICKS public site (no auth)
export const publicApi = {
  config: () => http.get(`/public/config`).then(r => r.data),
  identify: (input) => http.post(`/public/identify`, { input }).then(r => r.data),
  barcode: (code) => http.post(`/public/barcode`, { code }, { timeout: 30000 }).then(r => r.data),
  parseSpreadsheet: (file) => { const fd = new FormData(); fd.append("file", file); return http.post(`/public/parse-spreadsheet`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }).then(r => r.data); },
  quote: (setNumbers, quantities, quoteId) => http.post(`/public/quote`, { set_numbers: setNumbers, quantities, quote_id: quoteId || null }, { timeout: 120000 }).then(r => r.data),
  accept: (payload) => http.post(`/public/accept`, payload).then(r => r.data),
  invoice: (orderNo, token) => http.get(`/public/invoice/${orderNo}/${token}`).then(r => r.data),
};

// OFFERS4BRICKS customer accounts (Google login, separate o4b_session cookie)
export const accountApi = {
  exchange: (sessionId) => http.post(`/public/account/session`, { session_id: sessionId }).then(r => r.data),
  me: () => http.get(`/public/account/me`).then(r => r.data),
  logout: () => http.post(`/public/account/logout`).then(r => r.data),
  getDraft: () => http.get(`/public/account/draft`).then(r => r.data),
  putDraft: (draft) => http.put(`/public/account/draft`, draft).then(r => r.data),
  clearDraft: () => http.delete(`/public/account/draft`).then(r => r.data),
  submissions: () => http.get(`/public/account/submissions`).then(r => r.data),
};

// Admin auth (Emergent Google) + Offers4Bricks admin
export const authApi = {
  exchange: (sessionId) => http.post(`/auth/session`, { session_id: sessionId }).then(r => r.data),
  me: () => http.get(`/auth/me`).then(r => r.data),
  logout: () => http.post(`/auth/logout`).then(r => r.data),
  status: () => http.get(`/auth/status`).then(r => r.data),
};
export const adminApi = {
  publicSettings: () => http.get(`/admin/public-settings`).then(r => r.data),
  savePublicSettings: (patch) => http.put(`/admin/public-settings`, patch).then(r => r.data),
  requests: (params) => http.get(`/admin/offer-requests`, { params: params || {} }).then(r => r.data),
  patchRequest: (id, patch) => http.patch(`/admin/offer-requests/${id}`, patch).then(r => r.data),
  request: (id) => http.get(`/admin/offer-requests/${id}`).then(r => r.data),
  adjustPrices: (id, body) => http.patch(`/admin/offer-requests/${id}/prices`, body).then(r => r.data),
  deleteRequest: (id) => http.delete(`/admin/offer-requests/${id}`).then(r => r.data),
  removeRequestItem: (id, sn) => http.delete(`/admin/offer-requests/${id}/items/${encodeURIComponent(sn)}`).then(r => r.data),
  addRequestItem: (id, body) => http.post(`/admin/offer-requests/${id}/items`, body, { timeout: 90000 }).then(r => r.data),
  setItemMarket: (id, sn, body) => http.patch(`/admin/offer-requests/${id}/items/${encodeURIComponent(sn)}/market`, body, { timeout: 90000 }).then(r => r.data),
  uploadDeal: (formData) => http.post(`/admin/deals/upload`, formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 }).then(r => r.data),
  accelerlistCsvUrl: (id) => `${API}/admin/offer-requests/${id}/accelerlist.csv`,
  sendInvoiceEmail: (id) => http.post(`/admin/offer-requests/${id}/send-invoice`, {}, { timeout: 40000 }).then(r => r.data),
  quotes: () => http.get(`/admin/quotes`).then(r => r.data),
  barcodes: (unmatched) => http.get(`/admin/barcodes`, { params: unmatched ? { unmatched: true } : {} }).then(r => r.data),
  mapBarcode: (code, setNumber) => http.put(`/admin/barcodes/${code}`, { set_number: setNumber }).then(r => r.data),
  deleteBarcode: (code) => http.delete(`/admin/barcodes/${code}`).then(r => r.data),
  uiLayout: () => http.get(`/ui-layout`).then(r => r.data),
  saveUiLayout: (nav) => http.put(`/ui-layout`, { nav }).then(r => r.data),
  siteUsers: (params) => http.get(`/admin/site-users`, { params }).then(r => r.data),
  siteUserPerk: (email) => http.get(`/admin/site-users/${encodeURIComponent(email)}/perk`).then(r => r.data),
  saveSiteUserPerk: (email, body) => http.put(`/admin/site-users/${encodeURIComponent(email)}/perk`, body).then(r => r.data),
  siteUser: (email) => http.get(`/admin/site-users/${encodeURIComponent(email)}`).then(r => r.data),
  siteUsersCsvUrl: (params) => `${API}/admin/site-users/export.csv?${new URLSearchParams(Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== "" && v != null)))}`,
};

export const api = {
  // Dashboard
  dashboardStats: () => http.get(`/dashboard/stats`).then(r => r.data),

  // Catalog
  themes: () => http.get(`/catalog/themes`).then(r => r.data),
  sets: () => http.get(`/catalog/sets`).then(r => r.data),

  // Opportunities
  searchOpportunities: (filter) => http.post(`/opportunities/search`, filter).then(r => r.data),
  getOpportunity: (setNo) => http.get(`/opportunities/${setNo}`).then(r => r.data),
  exportCsv: (filter) => http.post(`/opportunities/export`, filter, { responseType: "blob" }).then(r => r.data),

  // ASIN Resolver
  asinResolverStats: (theme, themes) => http.get(`/asin-resolver/stats`, {
    params: theme ? { theme } : (themes?.length ? { themes } : {}),
    paramsSerializer: { indexes: null },
  }).then(r => r.data),
  asinResolverRun: (body) => http.post(`/asin-resolver/run`, body).then(r => r.data),
  asinResolverCancel: (jobId) => http.post(`/asin-resolver/cancel/${jobId}`).then(r => r.data),
  asinResolverLastIncomplete: () => http.get(`/asin-resolver/last-incomplete`).then(r => r.data),
  asinResolverResume: () => http.post(`/asin-resolver/resume`).then(r => r.data),
  asinReverify: (body) => http.post(`/asin-resolver/reverify`, body || {}, { timeout: 300000 }).then(r => r.data),

  // Catalog management
  uploadCatalog: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post(`/catalog/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }).then(r => r.data);
  },
  clearNonSeeded: () => http.delete(`/catalog/clear-non-seeded`).then(r => r.data),

  // API diagnostics
  diagnostics: () => http.get(`/diagnostics`).then(r => r.data),

  // API keys management
  getApiKeys: () => http.get(`/settings/keys`).then(r => r.data),
  updateApiKeys: (keys) => http.put(`/settings/keys`, { keys }).then(r => r.data),
  testApiKey: (provider) => http.post(`/settings/keys/test`, { provider }).then(r => r.data),

  // Scan cache maintenance
  scanCacheStats: () => http.get(`/scan-cache/stats`).then(r => r.data),
  scanCacheClear: (body) => http.post(`/scan-cache/clear`, body).then(r => r.data),

  // Set lookup (live single-set fetch)
  liveLookup: (setNo) => http.post(`/sets/lookup/${setNo}`).then(r => r.data),
  overrideAsin: (setNo, asin) => http.patch(`/sets/${setNo}/asin`, { asin }).then(r => r.data),

  // Scans
  listScans: () => http.get(`/scans`).then(r => r.data),
  createScan: (req) => http.post(`/scans`, req).then(r => r.data),
  getScan: (id) => http.get(`/scans/${id}`).then(r => r.data),
  previewPreviouslyPurchasedScan: () => http.get(`/scans/previously-purchased/preview`).then(r => r.data),
  scanPreviouslyPurchased: (live = true, fullAmazon = false) =>
    http.post(`/scans/previously-purchased?live=${live}&full_amazon=${fullAmazon}`).then(r => r.data),

  // Watchlists
  listWatchlists: () => http.get(`/watchlists`).then(r => r.data),
  createWatchlist: (data) => http.post(`/watchlists`, data).then(r => r.data),
  deleteWatchlist: (id) => http.delete(`/watchlists/${id}`).then(r => r.data),
  addToWatchlist: (id, setNo) => http.post(`/watchlists/${id}/items`, { set_number: setNo }).then(r => r.data),
  removeFromWatchlist: (id, setNo) => http.delete(`/watchlists/${id}/items/${setNo}`).then(r => r.data),
  watchlistOpps: (id) => http.get(`/watchlists/${id}/opportunities`).then(r => r.data),

  // Saved filters
  listSavedFilters: () => http.get(`/saved-filters`).then(r => r.data),
  createSavedFilter: (data) => http.post(`/saved-filters`, data).then(r => r.data),
  deleteSavedFilter: (id) => http.delete(`/saved-filters/${id}`).then(r => r.data),

  // Settings
  getSettings: () => http.get(`/settings`).then(r => r.data),
  updateSettings: (data) => http.patch(`/settings`, data).then(r => r.data),

  // Private Collection
  uploadCollection: (file, replace = false) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post(`/collection/upload?replace=${replace}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }).then(r => r.data);
  },
  collectionItems: () => http.get(`/collection/items`).then(r => r.data),
  scanCollection: (body) => http.post(`/collection/scan`, body).then(r => r.data),
  cancelCollectionScan: (jobId) => http.post(`/collection/scan/cancel/${jobId}`).then(r => r.data),
  removeCollectionItem: (setNo) => http.delete(`/collection/items/${setNo}`).then(r => r.data),
  clearCollection: () => http.delete(`/collection`).then(r => r.data),

  // Identify (image search)
  identifyImage: (file, mode = "sets", topK = 5) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post(`/identify/image?mode=${mode}&top_k=${topK}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    }).then(r => r.data);
  },
  identifyUrl: (url, mode = "sets", topK = 5) =>
    http.post(`/identify/url`, { url, mode, top_k: topK }).then(r => r.data),

  // Deals (active pipeline + past archive) — scoped to a deal list (tab)
  listDealLists: () => http.get(`/deal-lists`).then(r => r.data),
  createDealList: (name) => http.post(`/deal-lists`, { name }).then(r => r.data),
  renameDealList: (id, name) => http.patch(`/deal-lists/${id}`, { name }).then(r => r.data),
  reorderDealLists: (ids) => http.put(`/deal-lists/reorder`, { ids }).then(r => r.data),
  deleteDealList: (id) => http.delete(`/deal-lists/${id}`).then(r => r.data),
  uploadDeals: (file, status = "active", replace = false, autoSaveAsin = true, listId = null) => {
    const fd = new FormData();
    fd.append("file", file);
    const qs = `status=${status}&replace=${replace}&auto_save_asin=${autoSaveAsin}` + (listId ? `&list_id=${listId}` : "");
    return http.post(`/deals/upload?${qs}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }).then(r => r.data);
  },
  listDeals: (status = "active", pageSize = 5000, listId = null) =>
    http.get(`/deals`, { params: { status, page_size: pageSize, list_id: listId || undefined } }).then(r => r.data),
  deleteDeal: (id) => http.delete(`/deals/${id}`).then(r => r.data),
  approveDealOffer: (id, offer) => http.patch(`/deals/${id}`, { approve: true, approved_offer: offer }).then(r => r.data),
  unapproveDealOffer: (id) => http.patch(`/deals/${id}`, { approve: false }).then(r => r.data),
  approveDealsBulk: (items) => http.post(`/deals/approve-bulk`, { items }).then(r => r.data),
  anomalies: (includeReviewed) => http.get(`/anomalies`, { params: includeReviewed ? { include_reviewed: true } : {} }).then(r => r.data),
  resolveAnomaly: (sn, body) => http.post(`/anomalies/${sn}/resolve`, body).then(r => r.data),
  unreviewAnomaly: (sn) => http.delete(`/anomalies/${sn}/review`).then(r => r.data),
  sweepSummary: () => http.get(`/admin/asin-sweep/summary`).then(r => r.data),
  sweepList: (params) => http.get(`/admin/asin-sweep/list`, { params }).then(r => r.data),
  sweepRun: (body) => http.post(`/admin/asin-sweep/run`, body).then(r => r.data),
  sweepJob: () => http.get(`/admin/asin-sweep/job`).then(r => r.data),
  sweepCancel: (id) => http.post(`/admin/asin-sweep/job/${id}/cancel`).then(r => r.data),
  sweepRestore: (sn) => http.post(`/admin/asin-sweep/${encodeURIComponent(sn)}/restore`).then(r => r.data),
  setDealOffer: (id, offer) => http.patch(`/deals/${id}`, offer == null ? { clear_manual_offer: true } : { manual_offer: offer }).then(r => r.data),
  clearDeals: (status, listId = null) => http.delete(`/deals`, { params: { status, list_id: listId || undefined } }).then(r => r.data),

  // eBay (SerpApi) status
  ebayStatus: () => http.get(`/ebay/status`).then(r => r.data),
  ebayClearCache: () => http.delete(`/ebay/cache`).then(r => r.data),

  // Deals background scan (per-source)
  scanDeals: (source, status = "active", continuous = false, opts = {}) =>
    http.post(`/deals/scan`, {
      source,
      status,
      continuous,
      list_id: opts.listId || null,
      set_numbers: opts.setNumbers || null,
      missing_only: !!opts.missingOnly,
      asin_rows_only: !!opts.asinRowsOnly,
      retry_failed: !!opts.retryFailed,
    }).then(r => r.data),
  cancelDealsScan: (jobId) => http.post(`/deals/scan/cancel/${jobId}`).then(r => r.data),
  pushDealsToBoard: (listId) => http.post(`/deals/push-to-board`, null, { params: { list_id: listId || undefined }, timeout: 120000 }).then(r => r.data),
  setManualBlPrice: (setNumber, price, note) =>
    http.patch(`/sets/${encodeURIComponent(setNumber)}/manual-bl-price`, { price, note }).then(r => r.data),
  clearManualBlPrice: (setNumber) =>
    http.delete(`/sets/${encodeURIComponent(setNumber)}/manual-bl-price`).then(r => r.data),
  setManualPrice: (setNumber, field, price, note) =>
    http.patch(`/sets/${encodeURIComponent(setNumber)}/manual-price`, { field, price, note }).then(r => r.data),
  clearManualPrice: (setNumber, field) =>
    http.post(`/sets/${encodeURIComponent(setNumber)}/manual-price/clear`, { field }).then(r => r.data),
};
